import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Switch, FlexItem, TextInput, FormGroup, Alert, Button, TextInputGroup } from '@patternfly/react-core';

import ColorPicker from '../../components/color-picker';
import { removeScrollToViewFromRoiCustomization, StoreState, updateRoiCustomizationColor, updateRoiCustomizationIncludedInModel, updateRoiCustomizationName, updateRoiCustomizationOperation, updateRoiFmaId, updateRoiInterpretedType } from '../../store/reducers';
import { makeSelectFormValidationErrorsForRoi, selectCustomizationValidationError, selectGlobalRoiCustomizationById, selectOriginalRoiCustomizationById, selectRoiCustomizationById } from '../../store/selectors';
import { useScroll } from '../../hooks/useScroll';
import { CustomizationObjectType, isRoiCustomization } from '../../store/model-customization-types';
import TextOverrideField from '../../components/text-override-field';
import { ActionsColumn, IAction, Td, Tr } from '@patternfly/react-table';
import { columnCssNames, columnIds } from './RoiTable';
import InterpretedTypeDropdown from './InterpretedTypeDropdown';
import { useTranslation } from 'react-i18next';
import RoiOperationModal from '../../components/roi-operation-modal';
import { getFormValidationErrorMessage } from '../../store/form-errors';
import { defaultValidation, FormValidationState } from '../../components/form-validation-state';

interface RoiItemProps {
    roiId: string;
    isGlobalRoi?: boolean;
    hideIncludedOption?: boolean;
    hideIdentificationCodeFields?: boolean;
    extraColumnsBefore?: ReactNode[],
    extraColumnsAfter?: ReactNode[],
    rowActions?: IAction[];
}

const emptyOperationValidation: FormValidationState = { validated: 'error', helperTextInvalid: 'Operation must not be empty.' };

const RoiItem = (props: RoiItemProps) => {
    const { roiId, hideIncludedOption, hideIdentificationCodeFields, rowActions, extraColumnsBefore, extraColumnsAfter } = props;
    const isGlobalRoi = props.isGlobalRoi === true;

    const dispatch = useDispatch();
    const { t } = useTranslation();

    const roi = useSelector((state: StoreState) => isGlobalRoi ?
        selectGlobalRoiCustomizationById(state, roiId) :
        selectRoiCustomizationById(state, roiId));
    const originalRoi = useSelector((state: StoreState) => isGlobalRoi ? undefined : selectOriginalRoiCustomizationById(state, roiId));
    const validationError = useSelector((state: StoreState) => selectCustomizationValidationError(state, roiId));

    const selectFormValidationErrorsForRoi = useMemo(makeSelectFormValidationErrorsForRoi, []);
    const formValidationErrors = useSelector((state: StoreState) => selectFormValidationErrorsForRoi(state, roiId));

    const [executeScroll, scrollRef] = useScroll<HTMLDivElement>();
    const [isHighlightedItem, setIsHighlightedItem] = useState(false);

    /** Scroll the viewport to a correct position if an advanced ROI was just added. */
    useEffect(() => {
        if (roi && scrollRef && isRoiCustomization(roi) && roi.scrollToView) {
            setIsHighlightedItem(true);
            dispatch(removeScrollToViewFromRoiCustomization(roiId));
            executeScroll();
        }
    }, [executeScroll, roi, scrollRef, dispatch, roiId]);

    const [nameFieldValidation, setNameFieldValidation] = useState<FormValidationState>(defaultValidation);
    const [operationFieldValidation, setOperationFieldValidation] = useState<FormValidationState>(defaultValidation);
    const [isRoiOperationModalOpen, setIsRoiOperationModalOpen] = useState(false);

    /** Set non-blocking form validation errors generated in redux store reducers to be visible in this form's normal UI validation. */
    useEffect(() => {
        if (formValidationErrors.length > 0) {
            setNameFieldValidation({ validated: 'error', helperTextInvalid: formValidationErrors.map(e => getFormValidationErrorMessage(e.errorType)).join(' ') });
        } else {
            setNameFieldValidation(defaultValidation);
        }
    }, [formValidationErrors, roi]);

    const handleColorChange = useCallback((color: [number, number, number]) => {
        dispatch(updateRoiCustomizationColor(roiId, color, isGlobalRoi));
    }, [dispatch, isGlobalRoi, roiId]);

    const handleIncludedChange = useCallback((isIncluded: boolean) => {
        dispatch(updateRoiCustomizationIncludedInModel(roiId, isIncluded, isGlobalRoi));
    }, [dispatch, isGlobalRoi, roiId]);

    /** Handle changing ROI's name and perform minor string validation in the process. */
    const handleNameChange = useCallback((name: string) => {
        dispatch(updateRoiCustomizationName(roiId, name, isGlobalRoi));
    }, [dispatch, isGlobalRoi, roiId]);

    /** Handle changing ROI's operation and perform minor string validation in the process. */
    const handleOperationChanged = useCallback((operation: string) => {
        dispatch(updateRoiCustomizationOperation(roiId, operation, isGlobalRoi));
        if (operation) {
            setOperationFieldValidation(defaultValidation);
        } else {
            setOperationFieldValidation(emptyOperationValidation);
        }
    }, [dispatch, isGlobalRoi, roiId]);

    const handleInterpretedTypeChange = useCallback((interpretedOperation: string) => {
        dispatch(updateRoiInterpretedType(roiId, interpretedOperation, isGlobalRoi));
    }, [dispatch, isGlobalRoi, roiId]);

    const handleFmaCodeValueChanged = useCallback((value: string) => {
        dispatch(updateRoiFmaId(roiId, { hasFmaId: true, fmaIdCodeValue: value }, isGlobalRoi));
    }, [dispatch, isGlobalRoi, roiId]);

    const handleFmaCodeMeaningChanged = useCallback((value: string) => {
        dispatch(updateRoiFmaId(roiId, { hasFmaId: true, fmaIdCodeMeaning: value }, isGlobalRoi));
    }, [dispatch, isGlobalRoi, roiId]);

    const handleFmaCodeSchemeDesignatorChanged = useCallback((value: string) => {
        dispatch(updateRoiFmaId(roiId, { hasFmaId: true, fmaIdCodingSchemeDesignator: value }, isGlobalRoi));
    }, [dispatch, isGlobalRoi, roiId]);

    if (roi === undefined) {
        return null;
    }

    const isAdvancedRoiItem = !roi.isBuiltInRoi;

    const prefix = `${isGlobalRoi ? 'global-' : ''}roi-${roi.id}`;

    const nameFieldId = `${prefix}-name-override`;
    const colorFieldId = `${prefix}-color`;
    const includedFieldId = `${prefix}-included`;

    const highlight = isHighlightedItem ? 'highlighted-item' : '';

    const hasValidationError = validationError !== undefined && (validationError.type === CustomizationObjectType.Roi || validationError.type === CustomizationObjectType.PhysicalProperties);
    const validationErrorTitle = validationError && validationError.type === CustomizationObjectType.PhysicalProperties ? 'Validation error in physical properties settings' : 'Validation error';

    return (
        <>
            <Tr key={roiId} className={`roi-item ${hasValidationError ? 'validation-error' : undefined} ${highlight}`}>
                <Td className={columnCssNames.modified}><div className="roi-item-is-modified" title="This customization has unsaved changes.">{roi.isModified ? '*' : <>&nbsp;</>}</div></Td>

                {extraColumnsBefore && extraColumnsBefore.map(extraColumn => extraColumn)}

                <Td dataLabel={columnIds.name} className={columnCssNames.name}>
                    <div>
                        <FormGroup
                            fieldId={`field-${nameFieldId}`}
                            validated={nameFieldValidation.validated}
                            helperTextInvalid={nameFieldValidation.helperTextInvalid}
                            className={nameFieldValidation.validated === 'error' ? 'validation-error' : ''}
                        >
                            <div ref={scrollRef} className="roi-scroll-offset"></div>
                            {isAdvancedRoiItem ? (
                                <TextInput
                                    type="text"
                                    id={nameFieldId}
                                    onChange={handleNameChange}
                                    value={roi.name}
                                    validated={nameFieldValidation.validated}
                                />
                            ) : (
                                <TextOverrideField
                                    defaultText={originalRoi ? originalRoi.name : roi.name}
                                    textValue={roi.name}
                                    id={nameFieldId}
                                    onChange={handleNameChange}
                                    allowEmptyField={true}
                                />
                            )}
                        </FormGroup>
                    </div>
                </Td>

                {!hideIncludedOption && (
                    <Td dataLabel={columnIds.isIncluded} className={columnCssNames.isIncluded}>
                        <div>
                            <Switch
                                id={includedFieldId}
                                isChecked={roi.isIncluded}
                                onChange={handleIncludedChange}
                                label={t('toggle.roiIncludedInStructureSet.on')}
                                labelOff={t('toggle.roiIncludedInStructureSet.off')}
                            />
                        </div>
                    </Td>
                )}

                <Td dataLabel={columnIds.color} className={columnCssNames.color}>
                    <div>
                        <ColorPicker
                            color={roi.color}
                            onChange={handleColorChange}
                            textInputId={`${colorFieldId}-text-input`}
                        />
                    </div>
                </Td>

                <Td dataLabel={columnIds.operation} className={columnCssNames.operation}>
                    <div>
                        {isAdvancedRoiItem ? (
                            <FlexItem className="mv-flex-grow">
                                <div className="operation-column">
                                    <FormGroup
                                        fieldId={`field-operation-input-${roi.id}`}
                                        validated={operationFieldValidation.validated}
                                        helperTextInvalid={operationFieldValidation.helperTextInvalid}
                                        className={operationFieldValidation.validated === 'error' ? 'validation-error' : ''}
                                    >
                                        <TextInputGroup>
                                            <TextInput
                                                id={`operation-input-${roi.id}`}
                                                className=""
                                                type="text"
                                                onChange={handleOperationChanged}
                                                value={roi.operation}
                                            />
                                            <Button className='ellipsis-btn' variant="link" onClick={() => setIsRoiOperationModalOpen(true)}>
                                                ...
                                            </Button>

                                        </TextInputGroup>
                                    </FormGroup>
                                </div>
                            </FlexItem>
                        ) : (
                            <FlexItem className="mv-flex-grow">
                                <div className="operation-column">
                                    <TextInput
                                        id={`operation-input-${roi.id}`}
                                        className=""
                                        type="text"
                                        onChange={handleOperationChanged}
                                        value={roi.operation}
                                        isDisabled={true}
                                    />
                                </div>
                            </FlexItem>
                        )}
                    </div>
                </Td>

                <Td dataLabel={columnIds.interpretedType} className={columnCssNames.interpretedType}>
                    <div>
                        <InterpretedTypeDropdown value={roi.interpretedType} onSelect={handleInterpretedTypeChange} />
                    </div>
                </Td>

                {!hideIdentificationCodeFields && (
                    <>
                        <Td dataLabel={columnIds.fmaCodeValue} className={columnCssNames.fmaCodeValue}>
                            <div>
                                <TextInput
                                    id={`fmaCodeValue-input-${roi.id}`}
                                    type="text"
                                    onChange={handleFmaCodeValueChanged}
                                    value={roi.fmaIdCodeValue || ''}
                                />
                            </div>
                        </Td>

                        <Td dataLabel={columnIds.fmaCodeMeaning} className={columnCssNames.fmaCodeMeaning}>
                            <div>
                                <TextInput
                                    id={`fmaCodeMeaning-input-${roi.id}`}
                                    type="text"
                                    onChange={handleFmaCodeMeaningChanged}
                                    value={roi.fmaIdCodeMeaning || ''}
                                />
                            </div>
                        </Td>

                        <Td dataLabel={columnIds.fmaCodeSchemeDesignator} className={columnCssNames.fmaCodeSchemeDesignator}>
                            <div>
                                <TextInput
                                    id={`fmaCodeSchemeDesignator-input-${roi.id}`}
                                    type="text"
                                    onChange={handleFmaCodeSchemeDesignatorChanged}
                                    value={roi.fmaIdCodingSchemeDesignator || ''}
                                />
                            </div>
                        </Td>
                    </>
                )}

                {extraColumnsAfter && extraColumnsAfter.map(extraColumn => extraColumn)}

                <Td isActionCell>
                    {rowActions && <ActionsColumn items={rowActions} />}
                </Td>
            </Tr>

            {hasValidationError && (
                <Tr className="roi-item validation-error validation-error-box">
                    <Td dataLabel="Validation error" noPadding={true} colSpan={100}>
                        <Alert variant="danger" isInline isPlain isExpandable title={`${validationErrorTitle}: ${validationError.message}`}>
                            {validationError.field && (<div>Field: {validationError.field}</div>)}
                            <div>Error type: {validationError.detail}</div>
                            <div>Error ctx: {validationError.ctx}</div>
                        </Alert>
                    </Td>
                </Tr>
            )}

            {isRoiOperationModalOpen && (
                <RoiOperationModal isOpen={isRoiOperationModalOpen} onConfirm={handleOperationChanged} onClose={() => setIsRoiOperationModalOpen(false)} operation={roi.operation} />
            )}

        </>
    );
}

export default RoiItem;
